import {
  IBenefitWorklist,
  IComparisonChecksPage,
  IComparisonChecksRequest,
  IComparisonClusterDetail,
  IComparisonFilters,
  IComparisonSummary,
  IRecordVerdictRequest,
  IRescoreRequest,
  IRescoreResult,
  IVerificationTrend,
  IVerifiedFilters,
  IVerifiedStatus,
  IWorklistFilters,
} from "types/BenefitComparison/BenefitComparison";

import { api } from ".";

const toParams = (filter: object) =>
  Object.fromEntries(
    Object.entries(filter).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );

/**
 * CLE-2820 — the Engine worklist. Read-only but for two writes, SuperAdmin on the API.
 *
 * Every read carries verdict data, so all of them are tagged `BenefitVerdict` and both mutations
 * invalidate the tag — after a verdict or a re-run the scorecard, the worklist and the checks all
 * have to move together.
 *
 * 🚨 THE TREND IS NOT INVALIDATED BY THE RE-RUN, and that is deliberate: the re-run writes no
 * snapshot, so its line cannot have moved. Re-fetching it would only redraw the same points.
 */
const benefitComparisonQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * CLE-2820 follow-up — THE WORKLIST: one ranked row per cluster, merged and sorted server-side.
     * 🚨 All-time. There is no date window on this screen.
     */
    getBenefitWorklist: builder.query<
      { data: IBenefitWorklist; message: string },
      IWorklistFilters
    >({
      query: (filters) => ({
        url: "benefitComparison/worklist",
        params: toParams(filters),
      }),
      providesTags: ["BenefitVerdict"],
    }),
    /** CLE-2820 follow-up — one point per NIGHTLY run. Not filtered: a snapshot is the whole set. */
    getBenefitVerificationTrend: builder.query<
      { data: IVerificationTrend; message: string },
      { runs?: number }
    >({
      query: (request) => ({
        url: "benefitComparison/trend",
        params: toParams(request),
      }),
    }),
    getBenefitComparisonSummary: builder.query<
      { data: IComparisonSummary; message: string },
      IComparisonFilters
    >({
      query: (filters) => ({
        url: "benefitComparison/summary",
        params: toParams(filters),
      }),
      providesTags: ["BenefitVerdict"],
    }),
    getBenefitComparisonChecks: builder.query<
      { data: IComparisonChecksPage; message: string },
      IComparisonChecksRequest
    >({
      query: (request) => ({
        url: "benefitComparison/checks",
        params: toParams(request),
      }),
      providesTags: ["BenefitVerdict"],
    }),
    getBenefitComparisonCluster: builder.query<
      { data: IComparisonClusterDetail; message: string },
      IComparisonChecksRequest & { examplePverifyResponseId?: string }
    >({
      query: (request) => ({
        url: "benefitComparison/cluster",
        params: toParams(request),
      }),
      providesTags: ["BenefitVerdict"],
    }),
    /**
     * CLE-2821 — what today's engine makes of every verified check. Derived by the nightly run and
     * by the on-demand re-score, so both writes invalidate it.
     */
    getBenefitVerifiedStatus: builder.query<
      { data: IVerifiedStatus; message: string },
      IVerifiedFilters
    >({
      query: (filters) => ({
        url: "benefitComparison/verified",
        params: toParams(filters),
      }),
      providesTags: ["BenefitVerdict"],
    }),
    // 🚨 The reviewer is NOT in the body — the API takes it from the authenticated user.
    recordBenefitVerdict: builder.mutation<
      { data: { id: string }; message: string },
      IRecordVerdictRequest
    >({
      query: (body) => ({
        url: "benefitComparison/verdict",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BenefitVerdict"],
    }),
    /**
     * CLE-2820 follow-up — re-run today's engine over stored responses. Synchronous; seconds.
     *
     * 🚨 It calls pVerify for nothing, runs no new eligibility check, re-asks the AI nothing,
     * recomputes no legacy pick and changes no estimate that has already been sent.
     */
    rescoreBenefitChecks: builder.mutation<
      { data: IRescoreResult; message: string },
      IRescoreRequest
    >({
      query: (body) => ({
        url: "benefitComparison/rescore",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BenefitVerdict"],
    }),
  }),
});

export const {
  useGetBenefitWorklistQuery,
  useGetBenefitVerificationTrendQuery,
  useGetBenefitComparisonSummaryQuery,
  useGetBenefitComparisonChecksQuery,
  useGetBenefitComparisonClusterQuery,
  useGetBenefitVerifiedStatusQuery,
  useRecordBenefitVerdictMutation,
  useRescoreBenefitChecksMutation,
} = benefitComparisonQueries;
